import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  MapPin,
  DollarSign,
  Clock,
  TreePine,
  Users,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Filter,
  Loader2,
  Target,
  Hammer,
  Eye,
  TrendingUp,
  Award,
  Zap
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

// Status colors and labels
const statusConfig = {
  available: { color: 'bg-slate-500', label: 'Available', icon: Target },
  funded: { color: 'bg-green-500', label: 'Funded', icon: DollarSign },
  claimed: { color: 'bg-blue-500', label: 'In Progress', icon: Hammer },
  submitted: { color: 'bg-amber-500', label: 'Submitted', icon: Eye },
  verified: { color: 'bg-emerald-500', label: 'Verified', icon: CheckCircle2 },
  rejected: { color: 'bg-red-500', label: 'Rejected', icon: AlertCircle }
};

const difficultyConfig = {
  easy: { color: 'text-green-600', label: 'Easy' },
  medium: { color: 'text-amber-600', label: 'Medium' },
  hard: { color: 'text-red-600', label: 'Hard' }
};

// Zone Card Component
function ZoneCard({ zone }) {
  const status = statusConfig[zone.status] || statusConfig.available;
  const difficulty = difficultyConfig[zone.difficulty] || difficultyConfig.medium;
  const StatusIcon = status.icon;

  return (
    <Link to={`/marketplace/zone/${zone.zone_id}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -4 }}
        className="h-full"
      >
        <Card className="h-full overflow-hidden hover:shadow-lg transition-all cursor-pointer border-border hover:border-primary/50">
          {/* Status Banner */}
          <div className={`${status.color} px-4 py-2 flex items-center justify-between`}>
            <div className="flex items-center gap-2 text-white">
              <StatusIcon className="w-4 h-4" />
              <span className="text-sm font-medium">{status.label}</span>
            </div>
            <Badge variant="outline" className="bg-white/20 text-white border-white/30 text-xs">
              {zone.method === 'manual' ? 'Manual' : 'Mechanical'}
            </Badge>
          </div>

          <div className="p-4">
            {/* Title & Location */}
            <h3 className="font-bold text-foreground text-lg mb-1 line-clamp-1">
              {zone.name}
            </h3>
            <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
              {zone.description}
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <TreePine className="w-4 h-4 text-green-600" />
                <span>{zone.hectares || '?'} ha</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-blue-600" />
                <span>{zone.estimated_hours || '?'} hrs</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Target className={`w-4 h-4 ${difficulty.color}`} />
                <span className={difficulty.color}>{difficulty.label}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="w-4 h-4 text-amber-600" />
                <span className="font-semibold">${zone.bounty_target_amount?.toLocaleString()}</span>
              </div>
            </div>

            {/* Funding Progress */}
            {zone.status !== 'verified' && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">
                    ${zone.funded_amount?.toLocaleString() || 0} funded
                  </span>
                  <span className="font-medium text-primary">
                    {zone.funding_progress || 0}%
                  </span>
                </div>
                <Progress value={zone.funding_progress || 0} className="h-2" />
              </div>
            )}

            {/* CTA */}
            <div className="mt-4 flex items-center justify-between">
              {zone.status === 'available' && (
                <span className="text-sm text-primary font-medium flex items-center gap-1">
                  Fund this mission <ArrowRight className="w-4 h-4" />
                </span>
              )}
              {zone.status === 'funded' && (
                <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                  Ready to claim <ArrowRight className="w-4 h-4" />
                </span>
              )}
              {zone.status === 'claimed' && (
                <span className="text-sm text-blue-600 font-medium flex items-center gap-1">
                  Work in progress <Hammer className="w-4 h-4" />
                </span>
              )}
              {zone.status === 'submitted' && (
                <span className="text-sm text-amber-600 font-medium flex items-center gap-1">
                  Awaiting verification <Eye className="w-4 h-4" />
                </span>
              )}
              {zone.status === 'verified' && (
                <span className="text-sm text-emerald-600 font-medium flex items-center gap-1">
                  Mission complete <CheckCircle2 className="w-4 h-4" />
                </span>
              )}
            </div>
          </div>
        </Card>
      </motion.div>
    </Link>
  );
}

// Stats Card Component
function StatsCard({ icon: Icon, label, value, subvalue, color = 'primary' }) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    green: 'bg-green-500/10 text-green-600',
    amber: 'bg-amber-500/10 text-amber-600',
    blue: 'bg-blue-500/10 text-blue-600'
  };

  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
          {subvalue && <p className="text-xs text-primary mt-1">{subvalue}</p>}
        </div>
      </div>
    </Card>
  );
}

// Main Page Component
export default function MarketplacePage() {
  const { t } = useLanguage();
  const [zones, setZones] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');

  // Fetch zones
  const fetchZones = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (methodFilter !== 'all') params.append('method', methodFilter);

      const res = await fetch(`${API}/marketplace/zones?${params}`);
      const data = await res.json();
      setZones(data.zones || []);
    } catch (err) {
      console.error('Failed to fetch zones:', err);
      toast.error('Failed to load missions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, methodFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/marketplace/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchZones();
    fetchStats();
  }, [fetchZones, fetchStats]);

  // Filter zones by tab
  const getFilteredZones = () => {
    if (activeTab === 'all') return zones;
    if (activeTab === 'available') return zones.filter(z => z.status === 'available');
    if (activeTab === 'funded') return zones.filter(z => z.status === 'funded');
    if (activeTab === 'inprogress') return zones.filter(z => ['claimed', 'submitted'].includes(z.status));
    if (activeTab === 'completed') return zones.filter(z => z.status === 'verified');
    return zones;
  };

  const filteredZones = getFilteredZones();

  return (
    <div className="min-h-screen bg-background" data-testid="marketplace-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-16 pb-8 overflow-hidden">
        <div className="absolute inset-0 eco-gradient opacity-10" />
        <div className="relative max-w-6xl mx-auto px-4 pt-16">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <Zap className="w-3.5 h-3.5" />
              Eco-Task Marketplace
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Fund Missions. Protect Land.
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Direct impact through verified ecological work. Fund specific de-pining missions, 
              track progress in real-time, and see your contribution transform the landscape.
            </p>

            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/marketplace/transparency">
                <Button variant="outline" size="lg">
                  <Eye className="w-4 h-4 mr-2" />
                  View Transparency Feed
                </Button>
              </Link>
              <Link to="/worker">
                <Button variant="outline" size="lg">
                  <Users className="w-4 h-4 mr-2" />
                  Worker Portal
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      {stats && (
        <section className="py-8 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatsCard
                icon={Target}
                label="Total Missions"
                value={stats.zones?.total || 0}
                subvalue={`${stats.zones?.verified || 0} completed`}
                color="primary"
              />
              <StatsCard
                icon={DollarSign}
                label="Total Funded"
                value={`$${(stats.funding?.total_raised || 0).toLocaleString()}`}
                subvalue={`${stats.funding?.total_donors || 0} donors`}
                color="green"
              />
              <StatsCard
                icon={TreePine}
                label="Hectares Treated"
                value={stats.impact?.hectares_treated?.toFixed(1) || 0}
                subvalue="Native forest protected"
                color="amber"
              />
              <StatsCard
                icon={Award}
                label="Paid to Workers"
                value={`$${(stats.payouts?.total_paid || 0).toLocaleString()}`}
                subvalue={`${stats.workers?.active || 0} active workers`}
                color="blue"
              />
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <TabsList className="grid w-full sm:w-auto grid-cols-5">
                <TabsTrigger value="all" data-testid="tab-all">
                  All
                </TabsTrigger>
                <TabsTrigger value="available" data-testid="tab-available">
                  Available
                </TabsTrigger>
                <TabsTrigger value="funded" data-testid="tab-funded">
                  Funded
                </TabsTrigger>
                <TabsTrigger value="inprogress" data-testid="tab-inprogress">
                  In Progress
                </TabsTrigger>
                <TabsTrigger value="completed" data-testid="tab-completed">
                  Completed
                </TabsTrigger>
              </TabsList>

              {/* Filters */}
              <div className="flex gap-2">
                <Select value={methodFilter} onValueChange={setMethodFilter}>
                  <SelectTrigger className="w-[130px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Methods</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="mechanical">Mechanical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Zones Grid */}
            <div className="min-h-[400px]">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : filteredZones.length === 0 ? (
                <div className="text-center py-16">
                  <MapPin className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground text-lg">No missions found</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Check back soon for new opportunities
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredZones.map((zone, index) => (
                    <ZoneCard key={zone.zone_id} zone={zone} />
                  ))}
                </div>
              )}
            </div>
          </Tabs>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            How It Works
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Target, title: 'Choose Mission', desc: 'Browse available de-pining zones on the map' },
              { icon: DollarSign, title: 'Fund It', desc: 'Contribute any amount to reach the bounty goal' },
              { icon: Hammer, title: 'Work Gets Done', desc: 'Local crews claim and complete the mission' },
              { icon: CheckCircle2, title: 'Verified Impact', desc: 'Before/after proof verified by admin' }
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <step.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="text-xs text-muted-foreground mb-1">Step {i + 1}</div>
                <h3 className="font-semibold text-foreground mb-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 eco-gradient text-white/80">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <TreePine className="w-5 h-5" />
              </div>
              <span className="font-semibold text-lg text-white">Epuyén Renace</span>
            </div>
            <p className="text-sm opacity-70">
              © {new Date().getFullYear()} Epuyén Renace. Transparent ecological impact.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
